/**
 * P1 — thin API client over the package-2 backend. Attaches the owner's access
 * token (acquired silently, falling back to popup) and serialises errors. No license
 * logic here — the panel only calls /owner/* and the public /config (MASTER_BRIEF).
 */
import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import { apiRequest, graphRequest, msalInstance } from "../auth/msal";
import { config } from "../config";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function acquire(scopeReq: { scopes: string[] }): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new ApiError(401, "no_account", "Not signed in");
  try {
    const res = await msalInstance.acquireTokenSilent({ ...scopeReq, account });
    return res.accessToken;
  } catch (e) {
    // Silent acquisition fails when interaction is required OR when the hidden
    // iframe used for silent renew can't complete (third-party cookies blocked →
    // BrowserAuthError "monitor_window_timeout"). In both cases fall back to a popup.
    if (e instanceof InteractionRequiredAuthError || e instanceof BrowserAuthError) {
      const res = await msalInstance.acquireTokenPopup({ ...scopeReq, account });
      return res.accessToken;
    }
    throw e;
  }
}

const getToken = () => acquire(apiRequest);
const getGraphToken = () => acquire(graphRequest);

// ---- Microsoft Graph directory people-picker (Administratorzy) ----------------

export interface DirectoryUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
}

async function graphGet<T>(path: string): Promise<T> {
  const res = await fetch(`https://graph.microsoft.com/v1.0${path}`, {
    headers: { Authorization: `Bearer ${await getGraphToken()}` },
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const j = await res.json();
      message = j.error?.message ?? message;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, `graph_${res.status}`, message);
  }
  return (await res.json()) as T;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean; // default true
  raw?: boolean; // return text instead of JSON (for application/jwt)
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true, raw = false } = opts;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) headers.Authorization = `Bearer ${await getToken()}`;

  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let code = `http_${res.status}`;
    let message = res.statusText;
    try {
      const j = await res.json();
      code = j.error ?? code;
      message = j.message ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code, message);
  }

  if (raw) return (await res.text()) as unknown as T;
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ---- Endpoints (package 2) ---------------------------------------------------

export type Plan = "standard" | "governance";
export type LicenseStatus = "issued" | "active" | "revoked" | "expired" | "reactivate";

export interface LicenseRow {
  licenseId: string;
  plan: Plan;
  seats: number;
  status: LicenseStatus;
  tid: string | null;
  clientRef: string | null;
  issuedAt: string;
  activatedAt: string | null;
  expiresAt: string;
  installs: number;
}

export interface IssueResult {
  licenseId: string;
  key: string;
  plan: Plan;
  seats: number;
  expiresAt: string;
  setupLinkHint?: string;
}

export interface DemoIssueResult {
  licenseId: string;
  key?: string; // returned once; absent on a duplicate
  plan: Plan;
  seats: number;
  expiresAt: string;
  isDemo?: boolean;
  maxSites?: number;
  maxOneDrive?: number;
  duplicate?: boolean; // domain/email already had a demo (and force !== true)
  domain?: string; // the email domain the anti-abuse check keyed on
  message?: string;
  setupLinkHint?: string;
}

export interface DemoGrantRow {
  partitionKey: string; // "domain" | "email" (free providers) | "tenant"
  rowKey: string; // domainHash | emailHash | tid
  licenseId: string;
  grantedAt: string;
  activatedAt?: string;
  emailMasked?: string; // email/free partition
  email?: string; // full requester e-mail (owner console) — present for new grants
  domain?: string; // domain partition
  source?: string;
}

export interface DownloadLink {
  channel: string;
  version: string;
  url: string;
  sha256: string;
  expiresAt: string;
}

export interface ReleaseInfo {
  channel: string;
  version: string;
  notes: string;
  downloadUrl: string;
  sha256: string | null;
  publishedAt: string;
}

export interface AdminRow {
  oid: string;
  displayName: string;
  email: string;
  builtin: boolean;
  removable: boolean;
  addedAt: string;
  addedBy: string;
}

export interface ReleasesBundle {
  channel: string;
  current: ReleaseInfo | null;
  history: ReleaseInfo[];
}

export interface DownloadChannelStats {
  total: number;
  versions: Array<{ version: string; count: number; lastAt: string }>;
}
export interface DownloadStats {
  stable: DownloadChannelStats;
  beta: DownloadChannelStats;
}

export interface AppConfig {
  configVersion: number;
  currency: string;
  vatRate: number;
  plans: Array<{
    id: Plan; name: string; netYear: number;
    /** Net prices per licence period (months); missing/0 = period not offered. */
    prices?: { m1?: number; m3?: number; m12: number };
    features: string[]; featuresEn?: string[];
  }>;
  tos: { version: number; updatedAt: string; url: string; text: string; textEn?: string };
  privacy: { version: number; updatedAt: string; url: string; text: string; textEn?: string };
  returns: { version: number; updatedAt: string; url: string; text: string; textEn?: string };
  /** Coordinated Vulnerability Disclosure policy (public) — served at /security. */
  vulnDisclosure: { version: number; updatedAt: string; url: string; text: string; textEn?: string };
  /** Internal ENISA SRP reporting procedure (owner-only; never on public /config). */
  srpProcedure: { version: number; updatedAt: string; text: string; textEn?: string };
  support: { email: string; phone: string; url?: string; lifetimeYears?: number };
}

export interface Acceptance {
  kind: string;
  version: number;
  firstAcceptedAt: string;
  lastAcceptedAt: string;
  lastInstallId?: string;
  appVersion?: string;
}

export interface OrderRow {
  rowKey: string; // orderId
  orderNumber: string;
  company: string;
  email: string;
  nip: string;
  plan?: Plan;
  seats?: number;
  amountNet?: number;
  /** Shop orders: licence period (1 | 3 | 12 months), list price, gross and promo used. */
  periodMonths?: number;
  amountNetList?: number;
  amountGross?: number;
  promoCode?: string;
  discountPct?: number;
  currency?: string;
  invoiceNumber?: string;
  tid?: string;
  licenseId?: string;
  status: string;
  notes?: string;
  createdAt: string;
  // Set by the portal guest-checkout (POST /order) — provenance + accepted document versions.
  source?: string;
  acceptedAt?: string;
  acceptedTosVersion?: number;
  acceptedPrivacyVersion?: number;
  acceptedReturnsVersion?: number;
  keyMasked?: string; // masked license key (last chars only) — full key is never stored
  acceptance?: { tos: Acceptance | null; privacy: Acceptance | null };
}

export interface PromoRow {
  code: string;
  percent: number;
  active: boolean;
  validUntil?: string;
  maxUses?: number;
  uses: number;
  note?: string;
  createdAt: string;
  lastUsedAt?: string;
}
export interface PromoInput {
  code?: string;
  percent?: number;
  validUntil?: string | null;
  maxUses?: number | null;
  note?: string;
  active?: boolean;
}

export interface OrderInput {
  company?: string;
  email?: string;
  nip?: string;
  plan?: Plan;
  seats?: number;
  amountNet?: number;
  currency?: string;
  invoiceNumber?: string;
  tid?: string;
  licenseId?: string;
  status?: string;
  notes?: string;
}

export const api = {
  health: () => request<{ status: string }>("/health", { auth: false }),
  publicConfig: () => request<AppConfig>("/config", { auth: false }),

  listLicenses: () => request<{ count: number; licenses: LicenseRow[] }>("/owner/licenses"),
  issue: (input: { plan: Plan; seats: number; durationDays: number; clientRef?: string }) =>
    request<IssueResult>("/owner/issue", { method: "POST", body: input }),
  issueDemo: (input: { email: string; force?: boolean }) =>
    request<DemoIssueResult>("/owner/issue-demo", { method: "POST", body: input }),
  listDemos: () => request<{ demos: DemoGrantRow[] }>("/owner/demos"),
  listPromos: () => request<{ promos: PromoRow[] }>("/owner/promos"),
  createPromo: (input: PromoInput) => request<{ promo: PromoRow }>("/owner/promos", { method: "POST", body: input }),
  updatePromo: (code: string, input: PromoInput) =>
    request<{ promo: PromoRow }>(`/owner/promos/${encodeURIComponent(code)}`, { method: "PATCH", body: input }),
  deletePromo: (code: string) =>
    request<{ removed: boolean }>(`/owner/promos/${encodeURIComponent(code)}`, { method: "DELETE" }),
  removeDemoEmail: (email: string) =>
    request<{ removed: boolean }>("/owner/demos", { method: "DELETE", body: { email } }),
  renew: (input: { tid?: string; licenseId?: string; durationDays: number }) =>
    request<{ licenseId: string; expiresAt: string }>("/owner/renew", { method: "POST", body: input }),
  revoke: (input: { tid?: string; licenseId?: string; reason?: string }) =>
    request<{ licenseId: string; status: string }>("/owner/revoke", { method: "POST", body: input }),
  changePlan: (input: { tid?: string; licenseId?: string; plan: Plan; seats?: number }) =>
    request<{ licenseId: string; plan: Plan; seats: number }>("/owner/change-plan", { method: "POST", body: input }),
  regenerateKey: (input: { tid?: string; licenseId?: string }) =>
    request<{ licenseId: string; tid?: string; key: string }>("/owner/regenerate-key", { method: "POST", body: input }),
  downloadLink: (channel = "stable") =>
    request<DownloadLink>(`/owner/download-link?channel=${encodeURIComponent(channel)}`),
  getLatestRelease: (channel = "stable") =>
    request<ReleaseInfo>(`/releases/latest?channel=${encodeURIComponent(channel)}`, { auth: false }),
  setRelease: (input: { channel?: string; version: string; notes: string; downloadUrl: string; sha256?: string }) =>
    request<{ channel: string; version: string }>("/owner/releases", { method: "POST", body: input }),

  getConfig: () => request<AppConfig>("/owner/config"),
  saveConfig: (cfg: AppConfig) => request<AppConfig>("/owner/config", { method: "POST", body: cfg }),

  // Administratorzy
  listAdmins: () => request<{ admins: AdminRow[] }>("/owner/admins"),
  addAdmin: (input: { oid: string; displayName: string; email: string }) =>
    request<{ oid: string }>("/owner/admins", { method: "POST", body: input }),
  removeAdmin: (oid: string) =>
    request<{ removed: string }>(`/owner/admins/${encodeURIComponent(oid)}`, { method: "DELETE" }),

  // Aplikacja / pobieranie
  ownerReleases: (channel = "stable") =>
    request<ReleasesBundle>(`/owner/releases?channel=${encodeURIComponent(channel)}`),
  downloadStats: () => request<DownloadStats>("/owner/download-stats"),

  // Zamówienia
  listOrders: () => request<{ orders: OrderRow[] }>("/owner/orders"),
  createOrder: (input: OrderInput) =>
    request<OrderRow>("/owner/orders", { method: "POST", body: input }),
  updateOrder: (id: string, input: OrderInput) =>
    request<OrderRow>(`/owner/orders/${encodeURIComponent(id)}`, { method: "PATCH", body: input }),
  deleteOrder: (id: string) =>
    request<{ ok: boolean }>(`/owner/orders/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // Directory people-picker via Microsoft Graph (delegated User.ReadBasic.All)
  searchDirectory: async (query: string): Promise<DirectoryUser[]> => {
    const q = query.replace(/'/g, "''");
    const filter = `startswith(displayName,'${q}') or startswith(userPrincipalName,'${q}') or startswith(mail,'${q}')`;
    const path =
      `/users?$select=id,displayName,mail,userPrincipalName&$top=10&$filter=${encodeURIComponent(filter)}`;
    const res = await graphGet<{ value: DirectoryUser[] }>(path);
    return res.value;
  },
  getDirectoryUser: (oid: string) =>
    graphGet<DirectoryUser>(`/users/${encodeURIComponent(oid)}?$select=id,displayName,mail,userPrincipalName`),
};
